<template>
  <div class="signature-pad">
    <canvas
      ref="canvasRef"
      class="signature-pad__canvas"
      role="img"
      aria-label="Polje za potpis"
      @pointerdown="startDrawing"
      @pointermove="drawSignature"
      @pointerup="setSignatureValue"
      @pointercancel="setSignatureValue"
    />

    <p
      v-if="errorMessage"
      class="signature-pad__error text-hint"
      role="alert"
    >
      {{ errorMessage }}
    </p>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },

  errorMessage: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:modelValue'])

const canvasRef = ref(null)
let canvasContext = null
let isDrawing = false
let hasDrawnStroke = false
let resizeObserver

onMounted(() => {
  resizeObserver = new ResizeObserver(setCanvasSize)
  resizeObserver.observe(canvasRef.value)
})

onBeforeUnmount(() => resizeObserver.disconnect())

// The parent clears the value when the signing dialog is closed.
watch(() => props.modelValue, (signature) => {
  if (!signature) {
    setCanvasSize()
  }
})

function setCanvasSize () {
  const canvas = canvasRef.value
  const pixelRatio = window.devicePixelRatio

  canvas.width = Math.round(canvas.clientWidth * pixelRatio)
  canvas.height = Math.round(canvas.clientHeight * pixelRatio)
  isDrawing = false
  hasDrawnStroke = false
  canvasContext = canvas.getContext('2d')
  canvasContext.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
  canvasContext.lineCap = 'round'
  canvasContext.lineJoin = 'round'
  canvasContext.lineWidth = 2
  canvasContext.strokeStyle = '#000'

  if (props.modelValue) {
    const signatureImage = new Image()
    signatureImage.onload = () => {
      if (signatureImage.src !== props.modelValue) {
        return
      }

      const imageScale = Math.min(canvas.clientWidth / signatureImage.width, canvas.clientHeight / signatureImage.height)
      const scaledImageWidth = signatureImage.width * imageScale
      const scaledImageHeight = signatureImage.height * imageScale

      canvasContext.drawImage(signatureImage, (canvas.clientWidth - scaledImageWidth) / 2, (canvas.clientHeight - scaledImageHeight) / 2, scaledImageWidth, scaledImageHeight)
      hasDrawnStroke = true
    }
    signatureImage.src = props.modelValue
  }
}

function getPointerPosition (event) {
  const canvasBounds = canvasRef.value.getBoundingClientRect()

  return {
    x: (event.clientX - canvasBounds.left) * canvasRef.value.clientWidth / canvasBounds.width,
    y: (event.clientY - canvasBounds.top) * canvasRef.value.clientHeight / canvasBounds.height,
  }
}

function startDrawing (event) {
  const pointerPosition = getPointerPosition(event)

  isDrawing = true
  canvasRef.value.setPointerCapture(event.pointerId)
  canvasContext.beginPath()
  canvasContext.moveTo(pointerPosition.x, pointerPosition.y)
}

function drawSignature (event) {
  if (!isDrawing) {
    return
  }

  const pointerPosition = getPointerPosition(event)

  canvasContext.lineTo(pointerPosition.x, pointerPosition.y)
  canvasContext.stroke()
  hasDrawnStroke = true
}

function setSignatureValue () {
  if (!isDrawing) {
    return
  }

  isDrawing = false

  if (hasDrawnStroke) {
    emit('update:modelValue', canvasRef.value.toDataURL('image/png'))
  }
}
</script>

<style
  scoped
  lang="scss"
>
.signature-pad {
  position: relative;
  flex: 1;
  min-height: 0;

  &__canvas {
    display: block;
    width: 100%;
    height: 100%;
    cursor: crosshair;
    touch-action: none;
  }

  &__error {
    position: absolute;
    bottom: 8px;
    left: 16px;
    margin: 0;
    color: var(--color-danger);
  }
}
</style>
